'use client';

import { Coin, Info, CheckCircle, Warning, Scales, Crown } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { PromotionModal } from './promotion-modal';
import { TierCard } from '../loyalty/TierCard';
import { LevelsStepper } from '../loyalty/LevelsStepper';
import { getLoyaltyBalance, type LoyaltyBalance } from '@/lib/api/loyalty';
import { useMoneyRules } from '@/lib/money-rules';
import { formatPrice } from '@/lib/utils';

interface LoyaltyModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Условия с сервера (site_promotion_terms). Пусто или не передали —
   * показываем зашитый текст: пустой блок условий у публичной оферты хуже,
   * чем устаревший.
   */
  terms?: readonly string[];
  /** Правовая приписка с сервера (site_promotions.legal_note). */
  legalNote?: string | null;
}

/** Условия на 16.08.2026 — запасной текст, если сервер не ответил. */
const FALLBACK_TERMS = [
  'Начисляются только после доставки заказа',
  'Если списали коины — за заказ не начисляются',
  'Действуют 2 месяца с момента начисления',
  'Списание только после успешной оплаты',
];

/** Правовая приписка на 16.08.2026 — запасной текст. */
const FALLBACK_LEGAL_NOTE =
  'Программа лояльности не является публичной офертой. Организатор оставляет за собой право ' +
  'изменять условия, включая процент начисления, лимиты списания и срок действия коинов. ' +
  'Коины не являются электронными денежными средствами.';

export function LoyaltyModal({ open, onClose, terms, legalNote }: LoyaltyModalProps) {
  const [data, setData] = useState<LoyaltyBalance | null>(null);

  // Уровни, лимит списания и минимальная сумма приходят с сервера и правятся
  // в админке (раздел «Деньги»). Прежде здесь лежала своя копия лестницы
  // уровней и числа «15 %» и «1 500 ₽» текстом: после правки в админке модалка
  // молча продолжала обещать старые условия.
  //
  // Для гостя /loyalty/balance недоступен (нужен вход), поэтому подстраховка —
  // публичные money-rules, а не литералы в этом файле.
  const moneyRules = useMoneyRules();

  useEffect(() => {
    if (open) {
      getLoyaltyBalance().then(setData).catch(() => {});
    }
  }, [open]);

  const allLevels = data?.all_levels || moneyRules.loyalty.levels;
  const earnRate = data?.level?.earn_rate ?? 1;
  const maxSpendPercent = data?.max_spend_percent ?? moneyRules.loyalty.max_spend_percent;
  const minCartForSpend = data?.min_cart_for_spend ?? moneyRules.loyalty.min_cart_for_spend;

  return (
    <PromotionModal
      open={open}
      onClose={onClose}
      title="Программа лояльности"
      icon={<Coin className="w-5 h-5 md:w-6 md:h-6 text-white" weight="fill" />}
    >
      <div className="px-4 pt-5 pb-4 md:px-6 md:pt-6 md:pb-6 space-y-6 md:space-y-7">
        {/* Tier Card */}
        {data?.level ? (
          <TierCard level={data.level} />
        ) : (
          <div className="relative overflow-hidden rounded-card p-5 md:p-6" style={{ background: 'linear-gradient(135deg, var(--primary), #5A1FD6)' }}>
            <div className="absolute inset-0 opacity-[0.035] pointer-events-none" style={{
              backgroundImage: 'radial-gradient(circle at 1.5px 1.5px, white 1px, transparent 0)',
              backgroundSize: '20px 20px',
            }} />
            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-pill bg-white/[0.08] blur-3xl pointer-events-none" />
            <div className="relative flex items-center gap-3">
              <div className="w-11 h-11 rounded-card bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]">
                <Coin className="w-5 h-5 text-white" weight="fill" />
              </div>
              <div>
                <h3 className="text-base md:text-lg font-bold text-white tracking-tight">
                  Кэшбэк до 7% с каждого заказа
                </h3>
                <p className="text-sm text-white/50">
                  Войдите, чтобы увидеть свой уровень
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Levels Stepper */}
        <div>
          <h3 className="text-sm font-bold text-[var(--text-primary)] tracking-tight mb-3">
            Уровни кэшбэка
          </h3>
          <div className="p-4 rounded-card bg-[var(--surface-secondary)] border border-[var(--border-default)]">
            <LevelsStepper
              currentLevel={data?.level?.current ?? 0}
              allLevels={allLevels}
              level={data?.level ?? undefined}
            />
          </div>
          <p className="t-micro text-[var(--text-tertiary)] mt-2 text-center font-medium">
            Уровень определяется по общей сумме доставленных заказов
          </p>
        </div>

        {/* Split: Начисление + Списание */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Начисление */}
          <div className="rounded-card border border-[var(--border-default)] p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-card bg-[var(--primary)]/10 flex items-center justify-center">
                <Info className="w-3.5 h-3.5 text-[var(--primary)]" weight="fill" />
              </div>
              <h3 className="text-sm font-bold text-[var(--text-primary)] tracking-tight">Начисление</h3>
            </div>
            <div className="space-y-2.5">
              {[
                'Оформите и оплатите заказ',
                `После доставки — ${earnRate}% от суммы`,
                'Коины появятся в личном кабинете',
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <span className="t-nano font-mono font-bold text-[var(--primary)] mt-0.5 w-4 shrink-0">0{i + 1}</span>
                  <p className="t-caption font-normal text-[var(--text-secondary)] leading-relaxed">{step}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Списание */}
          <div className="rounded-card border border-[var(--border-default)] p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-card bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-500" weight="fill" />
              </div>
              <h3 className="text-sm font-bold text-[var(--text-primary)] tracking-tight">Списание</h3>
            </div>
            <div className="space-y-2.5">
              {[
                // Сервер считает и лимит, и порог от суммы ВМЕСТЕ С ДОСТАВКОЙ
                // (решение владельца по вопросу 3). Пишем это прямо, иначе
                // человек посчитает по товарам и не поймёт отказа.
                `До ${maxSpendPercent}% от суммы заказа с доставкой`,
                `Минимальная сумма заказа — ${formatPrice(minCartForSpend)} (с доставкой)`,
                '1 коин = 1 рубль скидки',
                'Включите при оформлении заказа',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-pill bg-emerald-500 mt-[7px] shrink-0" />
                  <p className="t-caption font-normal text-[var(--text-secondary)] leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Условия */}
        <div className="rounded-card border border-amber-500/15 bg-amber-500/[0.03] p-4">
          <div className="flex items-center gap-2 mb-2.5">
            <Warning className="w-4 h-4 text-amber-500" weight="fill" />
            <h3 className="text-sm font-bold text-[var(--text-primary)] tracking-tight">Условия</h3>
          </div>
          <div className="space-y-1.5">
            {(terms && terms.length > 0 ? terms : FALLBACK_TERMS).map((item, i) => (
              <div key={i} className="flex items-start gap-2 py-0.5">
                <div className="w-1 h-1 rounded-pill bg-amber-500/60 mt-[7px] shrink-0" />
                <p className="t-caption font-normal text-[var(--text-secondary)] leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Правовая информация */}
        <div className="p-4 rounded-card border border-[var(--border-default)] bg-[var(--surface-secondary)]/50">
          <div className="flex items-center gap-2 mb-2">
            <Scales className="w-3.5 h-3.5 text-[var(--text-tertiary)]" weight="fill" />
            <h4 className="t-nano font-semibold text-[var(--text-tertiary)] uppercase tracking-[0.1em]">
              Правовая информация
            </h4>
          </div>
          <p className="t-micro font-normal text-[var(--text-tertiary)] leading-relaxed">
            {legalNote || FALLBACK_LEGAL_NOTE}
          </p>
        </div>
      </div>
    </PromotionModal>
  );
}
